/**
 * OrbitPrint — one Location (the PC running OrbitPrint HUB) and its printers.
 * Approved prototype look: kit-edged card, status lamps with halo, ring gauge
 * per printer (green ring = ready, blue sweep = printing, amber mark = attention,
 * empty = offline). Copy stays honest: an offline location shows every printer
 * offline no matter what it last said. Managers rename, share, set the mode,
 * and disable / enable / remove; members only see what they may print to.
 */
import { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import { orbitApi, errText } from '../OrbitAuthContext';
import { usePlan } from '../plan';
import { Lamp } from './OrbitShell';
import SharingDialog from './SharingDialog';

export const MODE_OPTIONS = [
  { value: 'auto', label: 'Print right away' },
  { value: 'confirm', label: 'Ask before printing' },
  { value: 'view', label: 'Show the document, then ask' },
];

const STATUS = {
  ready:     { label: 'Ready',            lamp: 'ready' },
  printing:  { label: 'Printing',         lamp: 'busy' },
  offline:   { label: 'Offline',          lamp: 'offline' },
  attention: { label: 'Needs attention',  lamp: 'error' },
  unknown:   { label: 'Not reported yet', lamp: 'offline' },
};

function ago(iso) {
  if (!iso) return 'never';
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); } catch { return iso; }
}

export function RingGauge({ status }) {
  return (
    <svg className="orbit-ring" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle className="track" cx="12" cy="12" r="10" strokeWidth="2" />
      {status === 'ready' && <circle className="ready" cx="12" cy="12" r="10" strokeWidth="2" />}
      {status === 'printing' && <circle className="busy" cx="12" cy="12" r="10" strokeWidth="2" transform="rotate(-90 12 12)" />}
      {status === 'attention' && <path className="attn" d="M12 7v6M12 16v1" strokeWidth="2.2" strokeLinecap="round" />}
    </svg>
  );
}

/** Click-to-edit name. Enter saves, Escape cancels, blur saves. */
function InlineName({ value, onSave, className = '', testid, disabled }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  if (!editing || disabled) {
    return (
      <button type="button" className={`text-left ${disabled ? '' : 'hover:underline decoration-dotted underline-offset-4'} ${className}`}
        onClick={() => { if (!disabled) { setDraft(value); setEditing(true); } }} disabled={disabled}
        title={disabled ? undefined : 'Rename'} data-testid={testid} style={{ color: 'inherit', background: 'none', border: 0, padding: 0, font: 'inherit', cursor: disabled ? 'default' : 'text' }}>
        {value}
      </button>
    );
  }
  const commit = async () => {
    setEditing(false);
    const v = draft.trim();
    if (!v || v === value) return;
    await onSave(v);
  };
  return (
    <input autoFocus className={`orbit-input !h-auto !py-1 !px-2 ${className}`} value={draft}
      onFocus={(e) => e.target.select()}
      onChange={(e) => setDraft(e.target.value)} onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false); }}
      data-testid={`${testid}-input`} maxLength={60} />
  );
}

function PrinterRow({ printer, canManage, onChanged, onEditSharing }) {
  const st = STATUS[printer.status] || STATUS.unknown;
  const dimmed = !printer.present || !printer.shared || printer.paused_by_plan;
  const save = async (patch, okMsg) => {
    try {
      await orbitApi.put(`/printers/${printer.id}`, patch);
      if (okMsg) toast.success(okMsg);
      onChanged();
    } catch (err) { toast.error(errText(err)); }
  };
  return (
    <li className={`orbit-prow ${dimmed ? 'is-dim' : ''}`} data-testid={`orbit-printer-${printer.id}`}>
      <RingGauge status={printer.present ? printer.status : 'offline'} />
      <div className="min-w-0 flex-1">
        <InlineName value={printer.name} disabled={!canManage} className="font-semibold text-[15.5px]"
          onSave={(v) => save({ name: v }, 'Printer renamed')} testid={`orbit-printer-name-${printer.id}`} />
        <p className="orbit-mono text-[11px] truncate orbit-muted mt-0.5">
          {printer.windows_queue_name}
          {printer.has_preset ? ' · preset saved' : ''}
          {!printer.present ? ' · no longer on this PC' : ''}
          {printer.paused_by_plan ? ' · paused by plan' : ''}
          {printer.needs_approval ? ' · needs approval' : ''}
          {printer.status_detail ? ` · ${printer.status_detail}` : ''}
        </p>
      </div>
      <Lamp state={st.lamp} title={printer.status_detail || undefined} testid={`orbit-printer-status-${printer.id}`}>
        {st.label}{printer.status_detail ? ` · ${printer.status_detail}` : ''}
      </Lamp>
      {canManage && (
        <div className="flex flex-col items-end gap-1 flex-none">
          <label className="flex items-center gap-1.5 text-xs select-none cursor-pointer orbit-muted"
            title={printer.shared ? 'Shared — others in this organization can print here' : 'Not shared — only you see it'}>
            <input type="checkbox" className="orbit-check" checked={!!printer.shared} onChange={(e) => save({ shared: e.target.checked })}
              data-testid={`orbit-printer-shared-${printer.id}`} />
            Shared
          </label>
          <button type="button" className="orbit-link is-muted text-[11px]" onClick={() => onEditSharing(printer)}
            title="Choose who can print here directly" data-testid={`orbit-printer-sharing-${printer.id}`}>Edit sharing</button>
        </div>
      )}
    </li>
  );
}

function headline(location) {
  const ps = location.printers || [];
  if (location.status === 'disabled') return { lamp: 'offline', text: 'Disabled' };
  if (location.paused_by_user) return { lamp: 'offline', text: 'Paused' };
  if (location.paused_by_plan) return { lamp: 'error', text: 'Paused by plan' };
  if (!location.online) return { lamp: 'alarm offline', text: 'Offline' };
  const attn = ps.filter((p) => p.present && p.status === 'attention').length;
  if (attn) return { lamp: 'error', text: `${attn} need${attn === 1 ? 's' : ''} you` };
  const busy = ps.filter((p) => p.present && p.status === 'printing').length;
  if (busy) return { lamp: 'busy', text: `${busy} printing` };
  return { lamp: 'ready', text: 'All good' };
}

export default function LocationCard({ location, onChanged, delay = 0 }) {
  const [busy, setBusy] = useState(false);
  const [sharingFor, setSharingFor] = useState(null);
  const disabled = location.status === 'disabled';
  const canManage = location.can_manage;
  const head = headline(location);

  const act = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); if (okMsg) toast.success(okMsg); onChanged(); }
    catch (err) { toast.error(errText(err)); }
    finally { setBusy(false); }
  };
  const patch = (body, okMsg) => act(() => orbitApi.put(`/locations/${location.id}`, body), okMsg);
  const onDisable = () => {
    if (!window.confirm(`Disable "${location.name}"? Its HUB stops printing right away and nobody can pick its printers until you enable it again.`)) return;
    act(() => orbitApi.post(`/locations/${location.id}/disable`), 'Location disabled');
  };
  const onEnable = () => act(() => orbitApi.post(`/locations/${location.id}/enable`), 'Location enabled');
  const onRemove = () => {
    if (!window.confirm(`Remove "${location.name}" for good? Pair the HUB again if you need it back.`)) return;
    act(() => orbitApi.delete(`/locations/${location.id}`), 'Location removed');
  };
  // the owner's plan-slot switch (plans engine): activate takes a slot now, pause frees one and costs nothing
  const onActivate = () => act(() => orbitApi.post(`/locations/${location.id}/activate`), `${location.name} is active`);
  const onPause = () => {
    if (!window.confirm(`Pause "${location.name}"? It keeps its HUB, printers and presets, takes no plan slot, and nobody can print there until you make it active again.`)) return;
    act(() => orbitApi.post(`/locations/${location.id}/pause`), `${location.name} paused`);
  };

  const printers = location.printers || [];
  const paused = !disabled && (location.paused_by_plan || location.paused_by_user);
  const { plan } = usePlan();
  const cap = plan?.limits?.locations;
  return (
    <section className={`orbit-card is-hover orbit-boot p-6 pb-4 ${disabled || paused ? 'opacity-75' : ''}`} style={{ animationDelay: `${delay}ms` }} data-testid={`orbit-location-${location.id}`}
      data-paused={location.paused_by_plan ? 'plan' : location.paused_by_user ? 'user' : undefined}>
      <header className="flex flex-wrap items-start justify-between gap-3 mb-2">
        <div className="min-w-0">
          <InlineName value={location.name} disabled={!canManage || disabled}
            className={`orbit-display ${location.online && !disabled ? '' : 'orbit-ink2'}`} onSave={(v) => patch({ name: v }, 'Location renamed')}
            testid={`orbit-location-name-${location.id}`} />
          <div className="flex flex-wrap items-center gap-2.5 mt-1.5 text-xs orbit-muted">
            <span className={`orbit-dot ${location.online && !disabled ? '' : 'is-off'}`} />
            {disabled ? (
              <span className="orbit-mono">DISABLED</span>
            ) : location.online ? (
              <span className="orbit-mono" style={{ color: 'var(--ob-green)' }} data-testid={`orbit-location-online-${location.id}`}>ONLINE</span>
            ) : (
              <span className="orbit-mono" data-testid={`orbit-location-online-${location.id}`}>OFFLINE</span>
            )}
            <span>· {location.online ? 'seen' : 'last seen'} {ago(location.last_seen)}</span>
            {location.hostname ? <span className="orbit-mono">· {location.hostname}</span> : null}
            {location.app_version ? <span className="orbit-mono">· HUB {location.app_version}</span> : null}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {canManage && !disabled && (
            <label className="text-xs flex items-center gap-2 orbit-muted">
              <span className="hidden sm:inline">When a job arrives</span>
              <select className="orbit-input !text-sm !w-auto" value={location.mode} disabled={busy}
                onChange={(e) => patch({ mode: e.target.value }, 'Mode updated')} data-testid={`orbit-location-mode-${location.id}`}>
                {MODE_OPTIONS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </label>
          )}
          <Lamp state={head.lamp}>{head.text}</Lamp>
        </div>
      </header>

      <style>{`.orbit-card .orbit-display{font-size:30px}`}</style>

      {location.paused_by_plan && !location.paused_by_user && (
        <p className="mt-3 text-sm m-0 rounded-lg px-3 py-2" style={{ background: 'rgba(255,176,32,.10)', border: '1px solid rgba(255,176,32,.35)', color: 'var(--ob-ink-2)' }} data-testid={`orbit-location-paused-${location.id}`}>
          Paused on the {plan?.plan_label || 'current'} plan{cap != null ? ` — it includes ${cap} location${cap === 1 ? '' : 's'}` : ''}. Nothing here was deleted.
          {canManage ? ' Make it active to print here (another location pauses instead), or upgrade.' : ' Ask the owner to make it active, or to upgrade.'}
        </p>
      )}
      {location.paused_by_user && (
        <p className="mt-3 text-sm m-0 orbit-muted" data-testid={`orbit-location-paused-${location.id}`}>
          Paused by choice — it keeps its HUB, printers and presets, and takes no plan slot.{canManage ? ' Make it active to print here again.' : ''}
        </p>
      )}

      {printers.length === 0 ? (
        <p className="mt-3 text-sm orbit-ink2">
          {location.online
            ? 'No printers shared yet. Tick the printers to share in OrbitPrint HUB on this computer.'
            : 'No printers reported yet. They appear the first time the HUB connects.'}
        </p>
      ) : (
        <ul className="mt-2 list-none p-0 m-0">
          {printers.map((p) => <PrinterRow key={p.id} printer={p} canManage={canManage && !disabled} onChanged={onChanged} onEditSharing={setSharingFor} />)}
        </ul>
      )}

      {canManage && (
        <footer className="mt-3 pt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs orbit-muted" style={{ borderTop: '1px solid var(--ob-rule)' }}>
          {!disabled && (
            <label className="flex items-center gap-2">
              Review jobs that waited over
              <input type="number" min={0} max={168} className="orbit-input !h-8 !py-0 !px-2 !w-16 !text-sm" defaultValue={location.stale_hours}
                onBlur={(e) => { const v = parseInt(e.target.value, 10); if (!Number.isNaN(v) && v !== location.stale_hours) patch({ stale_hours: v }, 'Saved'); }}
                data-testid={`orbit-location-stale-${location.id}`} />
              hours
            </label>
          )}
          <span className="flex-1" />
          {disabled ? (
            <>
              <button className="orbit-link" onClick={onEnable} disabled={busy} data-testid={`orbit-location-enable-${location.id}`}>Enable</button>
              <button className="orbit-link is-danger" onClick={onRemove} disabled={busy} data-testid={`orbit-location-remove-${location.id}`}>Remove</button>
            </>
          ) : (
            <>
              {paused ? (
                <button className="orbit-link" onClick={onActivate} disabled={busy} data-testid={`orbit-location-activate-${location.id}`}>Make active</button>
              ) : (
                <button className="orbit-link is-muted" onClick={onPause} disabled={busy} title="Keeps everything, takes no plan slot" data-testid={`orbit-location-pause-${location.id}`}>Pause</button>
              )}
              <button className="orbit-link is-muted" onClick={onDisable} disabled={busy} data-testid={`orbit-location-disable-${location.id}`}>Disable</button>
            </>
          )}
        </footer>
      )}
      {sharingFor && <SharingDialog printer={sharingFor} onClose={() => setSharingFor(null)} onSaved={onChanged} />}
    </section>
  );
}
